<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { Plus, RotateCcwSquare, Trash2, Undo2 } from 'lucide-vue-next'
  import type {
    CropRegion,
    CroppedResult,
    DocumentAdjustments,
    EditMode,
    SelectedRegionId
  } from '../types'

  interface Props {
    editMode: EditMode
    cropRegions: CropRegion[]
    croppedResults: CroppedResult[]
    selectedCroppedRegionId: SelectedRegionId
    rotation: number
    croppedResultsRotation: number
    adjustments: Record<string, DocumentAdjustments>
    trimAmounts: Record<string, number>
    hasCropsApplied: boolean
  }

  interface Emits {
    (e: 'update:selectedCroppedRegionId', value: SelectedRegionId): void
    (e: 'setActiveRegion', regionId: string): void
    (e: 'removeRegionByIndex', index: number): void
    (e: 'addNewCropRegion'): void
    (e: 'applyCrop'): void
    (e: 'resetCrop'): void
    (e: 'rotateLeft'): void
    (e: 'rotateRight'): void
    (e: 'resetRotation'): void
    (e: 'updateAdjustment', key: keyof DocumentAdjustments, value: number): void
    (e: 'resetAdjustments'): void
    (e: 'applyTrimEdges', regionId: string, trimPercentage: number): void
    (e: 'resetTrim'): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  // Translations
  const { t } = useTranslation({
    en: {
      cropPages: 'Crop Pages',
      page: 'Page',
      removePage: 'Remove Page',
      addPage: 'Add page',
      applyCrop: 'Apply crop',
      resetCrop: 'Reset crop',
      rotatePages: 'Rotate pages',
      rotation: 'Rotation',
      rotateLeft: 'Rotate left',
      rotateRight: 'Rotate right',
      resetRotation: 'Reset rotation',
      resetAdjustments: 'Reset adjustments',
      adjustPages: 'Adjust pages',
      applyTo: 'Apply to:',
      allPages: 'All pages',
      left: 'Left',
      right: 'Right',
      adjustments: 'Adjustments',
      brightness: 'Brightness',
      contrast: 'Contrast',
      gamma: 'Gamma (Readability)',
      sharpness: 'Sharpness (Text Clarity)',
      saturation: 'Saturation',
      trimPages: 'Trim pages',
      trimEdges: 'Trim Edges',
      removeBorders: 'Remove borders',
      resetTrim: 'Reset trim',
      trimDescription:
        'Uniformly trim each page from all sides to remove borders and focus on content',
      trimAmount: 'Trim Amount',
      noTrim: 'No trim',
      maxTrim: 'Max trim',
      trimOriginalImage:
        'Trimming the original image. Create crop regions first for more precise control.'
    },
    de: {
      cropPages: 'Seiten zuschneiden',
      page: 'Seite',
      removePage: 'Seite entfernen',
      addPage: 'Seite hinzufügen',
      applyCrop: 'Zuschnitt anwenden',
      resetCrop: 'Zuschnitt zurücksetzen',
      rotatePages: 'Seiten drehen',
      rotation: 'Drehung',
      rotateLeft: 'Nach links drehen',
      rotateRight: 'Nach rechts drehen',
      resetRotation: 'Drehung zurücksetzen',
      resetAdjustments: 'Anpassungen zurücksetzen',
      adjustPages: 'Seiten anpassen',
      applyTo: 'Anwenden auf:',
      allPages: 'Alle Seiten',
      left: 'Links',
      right: 'Rechts',
      adjustments: 'Anpassungen',
      brightness: 'Helligkeit',
      contrast: 'Kontrast',
      gamma: 'Gamma (Lesbarkeit)',
      sharpness: 'Schärfe (Textklarheit)',
      saturation: 'Sättigung',
      trimPages: 'Seiten beschneiden',
      trimEdges: 'Ränder beschneiden',
      removeBorders: 'Ränder entfernen',
      resetTrim: 'Beschneidung zurücksetzen',
      trimDescription:
        'Jede Seite gleichmäßig von allen Seiten beschneiden, um Ränder zu entfernen und sich auf den Inhalt zu konzentrieren',
      trimAmount: 'Beschneidungsmenge',
      noTrim: 'Nicht beschneiden',
      maxTrim: 'Maximal beschneiden',
      trimOriginalImage:
        'Das ursprüngliche Bild wird beschnitten. Erstellen Sie zuerst Zuschneideregionen für eine präzisere Kontrolle.'
    }
  })

  function getRegionDisplayName(regionId: string): string {
    const regionIndex = props.cropRegions.findIndex((region) => region.id === regionId)
    return regionIndex !== -1 ? `${t('page')} ${regionIndex + 1}` : regionId
  }

  function getCurrentTrimAmount(): number {
    if (!props.selectedCroppedRegionId) {
      return props.trimAmounts['original'] || 0
    }
    if (props.selectedCroppedRegionId === 'all') {
      return props.trimAmounts['all'] || 0
    }
    return props.trimAmounts[props.selectedCroppedRegionId] || 0
  }

  function hasRotationChanges(): boolean {
    return props.rotation !== 0 || props.croppedResultsRotation !== 0
  }

  function hasAdjustmentChanges(): boolean {
    const currentPageId = props.selectedCroppedRegionId || 'all'

    if (currentPageId === 'all') {
      // When "All pages" is selected, check if ANY page has adjustments
      return Object.values(props.adjustments).some(
        (adj) =>
          adj.brightness !== 0 ||
          adj.contrast !== 0 ||
          adj.gamma !== 0 ||
          adj.sharpness !== 0 ||
          adj.saturation !== 0
      )
    } else {
      // For individual pages, check only that page's adjustments
      const currentAdj = getCurrentAdjustments()
      return (
        currentAdj.brightness !== 0 ||
        currentAdj.contrast !== 0 ||
        currentAdj.gamma !== 0 ||
        currentAdj.sharpness !== 0 ||
        currentAdj.saturation !== 0
      )
    }
  }

  function hasTrimChanges(): boolean {
    return Object.values(props.trimAmounts).some((amount) => amount > 0)
  }

  function handleResetRotation() {
    emit('resetRotation')
  }

  function getCurrentAdjustments(): DocumentAdjustments {
    const currentPageId = props.selectedCroppedRegionId || 'all'

    return (
      props.adjustments[currentPageId] || {
        brightness: 0,
        contrast: 0,
        gamma: 0,
        sharpness: 0,
        saturation: 0
      }
    )
  }
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Scrollable Content Area -->
    <div class="flex-1 overflow-y-auto">
      <!-- Crop Regions Management -->
      <div
        v-if="editMode === 'crop'"
        class="flex flex-col gap-4 p-4"
      >
        <h3 class="text-base font-medium text-black">{{ t('cropPages') }}</h3>
        <div class="flex max-h-80 flex-col gap-2 overflow-y-auto pr-2">
          <!-- All pages option - only show when crops have been applied -->
          <div
            v-if="hasCropsApplied"
            class="flex items-center gap-2"
          >
            <div
              class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
              :class="[
                selectedCroppedRegionId === 'all'
                  ? 'border-2 border-purple-600 bg-purple-100'
                  : 'border-gray-200 hover:bg-gray-50'
              ]"
              @click="$emit('update:selectedCroppedRegionId', 'all')"
            >
              <span class="text-sm font-medium text-black">{{ t('allPages') }}</span>
            </div>
          </div>
          <div
            v-for="(region, index) in cropRegions"
            :key="region.id"
            class="flex items-center gap-2"
          >
            <div
              class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
              :class="[
                (hasCropsApplied ? selectedCroppedRegionId === region.id : region.isActive)
                  ? 'border-2 border-purple-600 bg-purple-100'
                  : 'border-gray-200 hover:bg-gray-50'
              ]"
              @click="
                hasCropsApplied
                  ? $emit('update:selectedCroppedRegionId', region.id)
                  : $emit('setActiveRegion', region.id)
              "
            >
              <span class="text-sm font-medium text-black">{{ t('page') }} {{ index + 1 }}</span>
            </div>
            <button
              v-if="cropRegions.length > 1"
              class="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-500
                transition-colors hover:bg-gray-50 hover:text-gray-700"
              :title="`${t('removePage')} ${index + 1}`"
              @click.stop="$emit('removeRegionByIndex', index)"
            >
              <Trash2 class="h-4 w-4" />
            </button>
          </div>

          <!-- Add page button - only show when crops haven't been applied -->
          <button
            v-if="!hasCropsApplied"
            class="flex items-center gap-2 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm
              font-medium text-gray-600 hover:bg-gray-50"
            @click="$emit('addNewCropRegion')"
          >
            <Plus class="h-4 w-4" />
            {{ t('addPage') }}
          </button>
        </div>
      </div>

      <!-- Rotation controls -->
      <div
        v-if="editMode === 'rotate'"
        class="flex flex-col"
      >
        <!-- Rotate pages section -->
        <div class="flex flex-col gap-4 border-b border-gray-200 p-4">
          <h3 class="text-base font-medium text-black">{{ t('rotatePages') }}</h3>
          <div class="flex flex-col gap-2">
            <!-- All pages option - only show when there are 2+ cropped results -->
            <div
              v-if="croppedResults.length >= 2"
              class="flex items-center gap-2"
            >
              <div
                class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
                :class="[
                  selectedCroppedRegionId === 'all'
                    ? 'border-2 border-purple-600 bg-purple-100'
                    : 'border-gray-200 hover:bg-gray-50'
                ]"
                @click="$emit('update:selectedCroppedRegionId', 'all')"
              >
                <span class="text-sm font-medium text-black">{{ t('allPages') }}</span>
              </div>
            </div>
            <div
              v-for="result in croppedResults"
              :key="result.id"
              class="flex items-center gap-2"
            >
              <div
                class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
                :class="[
                  selectedCroppedRegionId === result.regionId
                    ? 'border-2 border-purple-600 bg-purple-100'
                    : 'border-gray-200 hover:bg-gray-50'
                ]"
                @click="$emit('update:selectedCroppedRegionId', result.regionId)"
              >
                <span class="text-sm font-medium text-black">
                  {{ getRegionDisplayName(result.regionId) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Rotation buttons section -->
        <div class="flex flex-1 flex-col gap-4 p-4">
          <h3 class="text-base font-medium text-black">{{ t('rotation') }}</h3>
          <div class="flex gap-2">
            <div
              class="flex flex-1 cursor-pointer flex-col items-center gap-1 rounded-lg border border-gray-200 bg-white
                px-2 py-3 shadow-sm hover:bg-gray-50"
              @click="$emit('rotateLeft')"
            >
              <RotateCcwSquare class="h-6 w-6 text-gray-800" />
              <span class="text-sm font-medium text-gray-800">{{ t('rotateLeft') }}</span>
            </div>
            <div
              class="flex flex-1 cursor-pointer flex-col items-center gap-1 rounded-lg border border-gray-200 bg-white
                px-2 py-3 shadow-sm hover:bg-gray-50"
              @click="$emit('rotateRight')"
            >
              <RotateCcwSquare class="h-6 w-6 scale-x-[-1] transform text-gray-800" />
              <span class="text-sm font-medium text-gray-800">{{ t('rotateRight') }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Adjust controls -->
      <div
        v-if="editMode === 'adjust'"
        class="flex flex-col"
      >
        <!-- Adjust pages section -->
        <div class="flex flex-col gap-4 border-b border-gray-200 p-4">
          <h3 class="text-base font-medium text-black">{{ t('adjustPages') }}</h3>
          <div class="flex flex-col gap-2">
            <!-- All pages option - only show when there are 2+ cropped results -->
            <div
              v-if="croppedResults.length >= 2"
              class="flex items-center gap-2"
            >
              <div
                class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
                :class="[
                  selectedCroppedRegionId === 'all'
                    ? 'border-2 border-purple-600 bg-purple-100'
                    : 'border-gray-200 hover:bg-gray-50'
                ]"
                @click="$emit('update:selectedCroppedRegionId', 'all')"
              >
                <span class="text-sm font-medium text-black">{{ t('allPages') }}</span>
              </div>
            </div>
            <div
              v-for="result in croppedResults"
              :key="result.id"
              class="flex items-center gap-2"
            >
              <div
                class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
                :class="[
                  selectedCroppedRegionId === result.regionId
                    ? 'border-2 border-purple-600 bg-purple-100'
                    : 'border-gray-200 hover:bg-gray-50'
                ]"
                @click="$emit('update:selectedCroppedRegionId', result.regionId)"
              >
                <span class="text-sm font-medium text-black">
                  {{ getRegionDisplayName(result.regionId) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Adjustments section -->
        <div class="flex flex-col p-4">
          <h3 class="text-base font-medium text-black">{{ t('adjustments') }}</h3>

          <div class="flex flex-col gap-0">
            <div>
              <label class="mb-0 block text-sm font-medium text-gray-700">
                {{ t('brightness') }}
              </label>
              <input
                :value="getCurrentAdjustments().brightness"
                type="range"
                min="-50"
                max="50"
                step="1"
                class="w-full"
                @input="
                  $emit(
                    'updateAdjustment',
                    'brightness',
                    Number(($event.target as HTMLInputElement).value)
                  )
                "
              />
              <div class="mt-1 text-center text-sm text-gray-600">
                {{ getCurrentAdjustments().brightness > 0 ? '+' : ''
                }}{{ getCurrentAdjustments().brightness }}
              </div>
            </div>

            <div>
              <label class="mb-0 block text-sm font-medium text-gray-700">
                {{ t('contrast') }}
              </label>
              <input
                :value="getCurrentAdjustments().contrast"
                type="range"
                min="-50"
                max="50"
                step="1"
                class="w-full"
                @input="
                  $emit(
                    'updateAdjustment',
                    'contrast',
                    Number(($event.target as HTMLInputElement).value)
                  )
                "
              />
              <div class="mt-1 text-center text-sm text-gray-600">
                {{ getCurrentAdjustments().contrast > 0 ? '+' : ''
                }}{{ getCurrentAdjustments().contrast }}
              </div>
            </div>

            <div>
              <label class="mb-0 block text-sm font-medium text-gray-700">{{ t('gamma') }}</label>
              <input
                :value="getCurrentAdjustments().gamma"
                type="range"
                min="-20"
                max="20"
                step="1"
                class="w-full"
                @input="
                  $emit(
                    'updateAdjustment',
                    'gamma',
                    Number(($event.target as HTMLInputElement).value)
                  )
                "
              />
              <div class="mt-1 text-center text-sm text-gray-600">
                {{ getCurrentAdjustments().gamma > 0 ? '+' : ''
                }}{{ getCurrentAdjustments().gamma }}
              </div>
            </div>

            <div>
              <label class="mb-0 block text-sm font-medium text-gray-700">
                {{ t('sharpness') }}
              </label>
              <input
                :value="getCurrentAdjustments().sharpness"
                type="range"
                min="0"
                max="100"
                step="1"
                class="w-full"
                @input="
                  $emit(
                    'updateAdjustment',
                    'sharpness',
                    Number(($event.target as HTMLInputElement).value)
                  )
                "
              />
              <div class="mt-1 text-center text-sm text-gray-600">
                {{ getCurrentAdjustments().sharpness }}
              </div>
            </div>

            <div>
              <label class="mb-0 block text-sm font-medium text-gray-700">
                {{ t('saturation') }}
              </label>
              <input
                :value="getCurrentAdjustments().saturation"
                type="range"
                min="-50"
                max="50"
                step="1"
                class="w-full"
                @input="
                  $emit(
                    'updateAdjustment',
                    'saturation',
                    Number(($event.target as HTMLInputElement).value)
                  )
                "
              />
              <div class="mt-1 text-center text-sm text-gray-600">
                {{ getCurrentAdjustments().saturation > 0 ? '+' : ''
                }}{{ getCurrentAdjustments().saturation }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trim controls -->
      <div
        v-if="editMode === 'trim'"
        class="flex flex-col"
      >
        <!-- Trim pages section -->
        <div class="flex flex-col gap-4 border-b border-gray-200 p-4">
          <h3 class="text-base font-medium text-black">{{ t('trimPages') }}</h3>
          <div class="flex flex-col gap-2">
            <!-- All pages option - only show when there are 2+ cropped results -->
            <div
              v-if="croppedResults.length >= 2"
              class="flex items-center gap-2"
            >
              <div
                class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
                :class="[
                  selectedCroppedRegionId === 'all'
                    ? 'border-2 border-purple-600 bg-purple-100'
                    : 'border-gray-200 hover:bg-gray-50'
                ]"
                @click="$emit('update:selectedCroppedRegionId', 'all')"
              >
                <span class="text-sm font-medium text-black">{{ t('allPages') }}</span>
              </div>
            </div>
            <div
              v-for="result in croppedResults"
              :key="result.id"
              class="flex items-center gap-2"
            >
              <div
                class="flex-1 cursor-pointer rounded-md border px-3 py-1.5 shadow-sm transition-all"
                :class="[
                  selectedCroppedRegionId === result.regionId
                    ? 'border-2 border-purple-600 bg-purple-100'
                    : 'border-gray-200 hover:bg-gray-50'
                ]"
                @click="$emit('update:selectedCroppedRegionId', result.regionId)"
              >
                <span class="text-sm font-medium text-black">
                  {{ getRegionDisplayName(result.regionId) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Remove borders section -->
        <div class="flex flex-1 flex-col gap-4 p-4">
          <h3 class="text-base font-medium text-black">{{ t('removeBorders') }}</h3>
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-gray-700">{{ t('trimAmount') }}</label>
            <!-- Simple trim slider -->
            <input
              :value="getCurrentTrimAmount()"
              type="range"
              min="0"
              max="25"
              step="1"
              class="w-full"
              @input="
                $emit(
                  'applyTrimEdges',
                  selectedCroppedRegionId || 'original',
                  Number(($event.target as HTMLInputElement).value)
                )
              "
            />
            <div class="text-center text-lg font-medium text-gray-900">
              {{ getCurrentTrimAmount() }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reset buttons - positioned at bottom for all modes -->
    <div class="mt-auto p-4">
      <!-- Reset rotation button -->
      <button
        v-if="editMode === 'rotate'"
        :disabled="!hasRotationChanges()"
        class="flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2
          text-sm font-medium transition-all"
        :class="[
          hasRotationChanges()
            ? 'text-gray-700 hover:bg-gray-50'
            : 'cursor-not-allowed text-gray-400'
        ]"
        @click="handleResetRotation"
      >
        <Undo2 class="h-4 w-4" />
        {{ t('resetRotation') }}
      </button>

      <!-- Reset adjustments button -->
      <button
        v-if="editMode === 'adjust'"
        :disabled="!hasAdjustmentChanges()"
        class="flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2
          text-sm font-medium transition-all"
        :class="[
          hasAdjustmentChanges()
            ? 'text-gray-700 hover:bg-gray-50'
            : 'cursor-not-allowed text-gray-400'
        ]"
        @click="$emit('resetAdjustments')"
      >
        <Undo2 class="h-4 w-4" />
        {{ t('resetAdjustments') }}
      </button>

      <!-- Reset trim button -->
      <button
        v-if="editMode === 'trim'"
        :disabled="!hasTrimChanges()"
        class="flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2
          text-sm font-medium transition-all"
        :class="[
          hasTrimChanges() ? 'text-gray-700 hover:bg-gray-50' : 'cursor-not-allowed text-gray-400'
        ]"
        @click="$emit('resetTrim')"
      >
        <Undo2 class="h-4 w-4" />
        {{ t('resetTrim') }}
      </button>

      <!-- Apply/Reset crop button -->
      <button
        v-if="editMode === 'crop' && !hasCropsApplied"
        class="w-full rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
        @click="$emit('applyCrop')"
      >
        {{ t('applyCrop') }}
      </button>
      <button
        v-if="editMode === 'crop' && hasCropsApplied"
        class="flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-4 py-2
          text-sm font-medium text-gray-700 hover:bg-gray-50"
        @click="$emit('resetCrop')"
      >
        <Undo2 class="h-4 w-4" />
        {{ t('resetCrop') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
  /* Enhanced range slider styling - Override Tailwind Forms plugin */
  input[type='range'] {
    -webkit-appearance: none !important;
    appearance: none !important;
    height: 6px !important;
    border-radius: 3px !important;
    background: #e5e7eb !important;
    outline: none !important;
    padding: 0 !important;
  }

  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none !important;
    appearance: none !important;
    width: 18px !important;
    height: 18px !important;
    border-radius: 50% !important;
    background: hsl(271, 81%, 56%) !important;
    cursor: pointer !important;
    border: none !important;
    margin-top: -6px !important;
  }

  input[type='range']::-moz-range-thumb {
    width: 18px !important;
    height: 18px !important;
    border-radius: 50% !important;
    background: hsl(271, 81%, 56%) !important;
    cursor: pointer !important;
    border: none !important;
    -moz-appearance: none !important;
  }

  input[type='range']::-webkit-slider-runnable-track {
    height: 6px !important;
    border-radius: 3px !important;
    background: #e5e7eb !important;
    border: none !important;
  }

  input[type='range']::-moz-range-track {
    height: 6px !important;
    border-radius: 3px !important;
    background: #e5e7eb !important;
    border: none !important;
  }

  /* Custom scrollbar styling */
  .overflow-y-auto::-webkit-scrollbar {
    width: 6px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
</style>
